import { OptionHelpers } from "../helpers/option-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { IChartDataService } from "../models/base/chart-data-service.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { TimeframeOptionModel } from "../models/drop-down-list/timeframe-option.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { IComponent } from "./component.interface";

export class TILogoComponent implements IComponent {
    public id = StringHelpers.newGuid();

    private element: HTMLElement;
    private logoElement: HTMLElement | null;

    private chartService: IChartDataService;

    private symbol = '';
    private label = '';
    private timeframe = Timeframe.OneMinute;
    private chartStyleSettings: ChartStyleSettingsModel | null = null;
    private base: IChartContainer;
    private screenSizeChanged = true;

    private overflowTimer: any;
    
    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.logoElement = this.element.findFirst('p.logo');
        this.base = base;
        this.chartService = base.chartService;

        this.onResize();
        this.subscribeTICompanyLogoData();
        this.subscribeChartStyleData();
        this.base.tiChartSeries.subscribeRedrawChange(this.id, () => {
            this.setPanelStyle();
        });
    }

    public show(timeframe: Timeframe) {
        this.symbol = this.chartService.getSymbolLabel();
        this.timeframe = timeframe;
        this.setSymbolPanel();
    }

    public hide() {
        this.element.addClass('hidden');
    }

    private subscribeTICompanyLogoData() {
        this.chartService.subscribeTICompanyLogoData((data: string) => {
            this.element.findFirst('.ti-logo')?.setInnerHtml(`<img src=${data} alt="" />`);
        });
    }

    private subscribeChartStyleData() {
        this.chartService.subscribeChartStyleData((chartStyleSettings: ChartStyleSettingsModel) => {
            this.chartStyleSettings = chartStyleSettings;
            if (this.logoElement) {
                const alpha = this.chartStyleSettings.watermarkOpacity / 100;
                const color = this.chartStyleSettings.watermarkColor.toRGBA(alpha);
                this.logoElement.style.color = color;
            }

            this.setPanelStyle();
        });
    }

    private setSymbolPanel() {
        const currentTimeframe = OptionHelpers.getTimeframeOptions().find((x: TimeframeOptionModel) => x.value == this.timeframe);
        this.label = this.symbol + ' , ' + currentTimeframe?.symbol;
        this.setPanelStyle();
    }

    public setPanelStyle() {
        if (!this.chartStyleSettings?.showWatermark) {
            this.hide();
            return;
        }

        if (this.base.marketingWebChartMode) {
            this.setTIWebsiteWatermark();
        } else {
            this.setTIProWatermark();
        }
    }

    private setTIWebsiteWatermark() {
        this.logoElement?.setInnerHtml(this.label);
        this.element.removeClass('hidden');

        if (!this.base.tiChartSeries?.highStockChart?.yAxis?.length) {
            return;
        }

        const mainYAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
        this.element.style.height = mainYAxis.height + 'px';
        
        const smallChart = this.base.isExtraSmall();
        if (smallChart) {
            this.element.style.marginTop = '0px';
            this.logoElement.style.fontSize = `42px`;
            return;
        }

        const padding = 40;
        const containerWidth = this.element.clientWidth;
        const containerHeight = this.element.clientHeight;
        const maxTextWidth = containerWidth - padding;
        const maxTextHeight = containerHeight - padding;

        let fontSizeWidth = 0;
        let fontSizeHeight = 0;

        if (this.base.pnpMode) {
            this.element.style.marginTop = 26 + 'px';

            fontSizeWidth = maxTextWidth * 0.50;
            fontSizeHeight = maxTextHeight * 0.50;

            // Set the font size to be the minimum of the width-based and height-based font sizes
            let fontSize = Math.min(fontSizeWidth, fontSizeHeight);

            this.logoElement.style.fontSize = `${fontSize}px`;

            // fail safe
            if (this.logoElement.scrollWidth > maxTextWidth) {
                fontSizeWidth = maxTextWidth * 0.18;
                fontSizeHeight = maxTextHeight * 0.25;

                fontSize = Math.min(fontSizeWidth, fontSizeHeight);
                this.logoElement.style.fontSize = `${fontSize}px`;
            }

            // fail safe2 
            if (this.element.scrollWidth > this.element.clientWidth) {
                fontSizeWidth = maxTextWidth * 0.20;
                fontSizeHeight = maxTextHeight * 0.25;

                fontSize = Math.min(fontSizeWidth, fontSizeHeight);
                this.logoElement.style.fontSize = `${fontSize}px`;
            }
        } else {
            let top = 46;

            fontSizeWidth = maxTextWidth * 0.35;
            fontSizeHeight = maxTextHeight * 0.30;
            this.element.style.marginTop = top + 'px';

            // Set the font size to be the minimum of the width-based and height-based font sizes
            let fontSize = Math.min(fontSizeWidth, fontSizeHeight);
            this.logoElement.style.fontSize = `${fontSize}px`;

            if (this.logoElement.scrollWidth > maxTextWidth) {
                fontSizeWidth = maxTextWidth * 0.25;
                fontSizeHeight = maxTextHeight * 0.20;

                fontSize = Math.min(fontSizeWidth, fontSizeHeight);
                this.logoElement.style.fontSize = `${fontSize}px`;
            }
        }
    }

    private setTIProWatermark() {
        this.logoElement?.setInnerHtml(this.label);
        this.element.removeClass('hidden');
        this.element.removeClass('four-digits');
        this.element.removeClass('five-digits');

        if (this.symbol.length > 4) {
            this.element.addClass('five-digits');
        } else if (this.symbol.length > 3) {
            this.element.addClass('four-digits');
        }

        const width = this.base.tiChartSeries.element.clientWidth;

        if (width < 300) {
            this.element.addClass('small-2x');
            this.element.removeClass('small');
        } else if (width < 768) {
            this.element.addClass('small');
            this.element.removeClass('small-2x');
        } else {
            this.element.removeClass('small');
            this.element.removeClass('small-2x');
        }

        if (!this.base.tiChartSeries?.highStockChart?.yAxis?.length) {
            return;
        }

        const chartStlye = this.base.tiChartSeries.chartStyleSettings;
        const mainYAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
        this.element.style.height = mainYAxis.height + 'px';

        if (this.base.pnpMode) {
            this.element.style.width = 'calc(100% - 55px)';

            if (this.screenSizeChanged) {
                this.logoElement.style.fontSize = '';
                clearTimeout(this.overflowTimer);
                this.overflowTimer = setTimeout(() => {
                    if (this.logoElement) {
                        if (this.logoElement.scrollWidth > this.element.clientWidth) {
                            this.logoElement.style.fontSize = '4vw';
                        }
                    }

                    this.screenSizeChanged = false;
                }, 300);
            }
        } else {
            let extraTop = this.base.tiChartSeries?.hasAnyComparisonSymbols() ? 58 : 46;

            if (this.base.isExtraSmall()) {
                extraTop = 20;
            } else if (this.base.element.containClass('one')) {
                extraTop = 28;
            }

            this.element.style.marginTop = extraTop + 'px';

            const chartExtraSmall = this.base.isExtraSmall();
            if (chartStlye?.showSmartRiskLevel && !chartExtraSmall) {
                this.element.style.width = 'calc(100% - 100px)';
            } else {
                this.element.style.width = 'calc(100% - 70px)';
            }
        }
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.screenSizeChanged = true;
                this.setPanelStyle();
            }, 300);
        });
    }
}